
import {
  defineComponent,
  PropType,
  ref,
  reactive,
  computed,
  watch,
  toRef,
  onMounted,
} from 'vue';
import { useI18n } from 'vue-i18n';

import Loader from '@/components/layout/Loader.vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import { PanelAccordion } from '@/components/flows/operators/shared';
import TemplateSelectorV2 from '@/components/automated-scenarios/fields/TemplateSelectorV2.vue';
import { TemplatePreview, VoucherConfig, MultiVoucherConfig, ReuseCouponSelector, UtmConfig } from '@/components/flows/operators/shared';
import { useSendingChannelForm } from '@/composables/flows';
import { useUtmData } from '@/components/flows/operators/shared/utmDefaults';
import { showToastError } from '@/helpers';
import type { SendWhatsappData } from './index';
import { createDefaultData } from './index';

export default defineComponent({
  name: 'ActionSendWhatsappForm',

  components: {
    Loader,
    Dialog,
    Button,
    PanelAccordion,
    TemplateSelectorV2,
    TemplatePreview,
    VoucherConfig,
    MultiVoucherConfig,
    ReuseCouponSelector,
    UtmConfig,
  },

  props: {
    modelValue: {
      type: Object as PropType<SendWhatsappData>,
      required: true,
      default: () => createDefaultData(),
    },

    formValidation: {
      type: Object,
      default: () => ({}),
    },

    selectedComponent: {
      type: String,
      default: '',
    },

    operatorId: {
      type: String,
      default: undefined,
    },
  },

  emits: ['update:modelValue', 'cancel'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const isLoading = ref(false);
    const panelsKey = ref(0);

    // Reactive data fields
    const dataFields = reactive<SendWhatsappData>(createDefaultData({ ...props.modelValue }));

    // Initialize sending channel form composable
    const sendingChannelForm = useSendingChannelForm({
      media: 'whatsapp',
      dataFields,
      formValidation: toRef(props, 'formValidation'),
      panelsKey,
      operatorId: props.operatorId,
      onCancel: () => emit('cancel'),
    });

    // Destructure commonly used values from composable
    const {
      campaignId,
      campaignType,
      templateConfig,
      templateSelectorIsOpen,
      formValidationErrors,
      voucherData,
      showVoucherPanel,
      showVoucherConfig,
      showReusePreviousCouponField,
      reusePreviousCouponOptions,
      useMultiVoucherConfig,
      resolvedParentVoucher,
      resolvedExpiringVoucher,
      handleSelectTemplate,
      handleCloseTemplateSelector,
      handleChangeTemplate,
      handleEditTemplate,
      showWhatsAppRevalidationModal,
      confirmWhatsAppRevalidation,
      cancelWhatsAppRevalidation,
      initialize,
    } = sendingChannelForm;

    // UTM data computed
    const utmData = useUtmData(dataFields);

    // Panels configuration (no message_config for whatsapp)
    const panels = computed(() => [
      {
        id: 'template_preview',
        title: 'automatedScenarios.actions.sending_channel.template_preview.title',
      },
      {
        id: 'voucher_config',
        title: 'automatedScenarios.actions.sending_channel.vouchers_config.title',
        hide: !showVoucherPanel.value,
      },
      {
        id: 'utm_config',
        title: 'automatedScenarios.actions.sending_channel.utm_config.title',
      },
    ]);

    // Emit updates when data changes
    watch(dataFields, () => {
      emit('update:modelValue', createDefaultData({ ...dataFields }));
    }, { deep: true });

    // Initialize on mount
    onMounted(async () => {
      try {
        isLoading.value = true;
        await initialize(dataFields.template_display_selector_id);
      } catch (error) {
        showToastError('GENERIC_ERROR');
      } finally {
        isLoading.value = false;
      }
    });

    return {
      t,
      isLoading,
      panelsKey,
      formValidationErrors,
      templateConfig,
      templateSelectorIsOpen,
      dataFields,
      voucherData,
      showVoucherPanel,
      showVoucherConfig,
      showReusePreviousCouponField,
      reusePreviousCouponOptions,
      useMultiVoucherConfig,
      resolvedParentVoucher,
      resolvedExpiringVoucher,
      panels,
      campaignId,
      campaignType,
      handleSelectTemplate,
      handleCloseTemplateSelector,
      handleChangeTemplate,
      handleEditTemplate,
      showWhatsAppRevalidationModal,
      confirmWhatsAppRevalidation,
      cancelWhatsAppRevalidation,
      utmData,
    };
  },
});
